<template>
  <div class="quad-viz-modern">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$AD // BC & AB = CD$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage" style="flex:1;">
        <svg width="100%" height="100%" viewBox="0 0 500 320" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-geo"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- Reference Axis of Symmetry -->
          <line x1="250" y1="20" x2="250" y2="300" stroke="rgba(255,255,255,0.1)" stroke-dasharray="10,5" />
          <text x="255" y="40" fill="rgba(255,255,255,0.3)" font-size="12">对称轴 L</text>

          <g transform="translate(250, 150)">
            <!-- The Shape Body -->
            <path :d="trapezoidPath" fill="rgba(56, 189, 248, 0.15)" stroke="#38bdf8" stroke-width="3" filter="url(#glow-geo)" />
            
            <!-- Parallel Indicators -->
            <g :transform="`translate(0, ${-h/2})`">
               <path d="M -10,-5 L 0,0 L -10,5" fill="none" stroke="#facc15" stroke-width="2" />
               <path d="M 10,-5 L 20,0 L 10,5" fill="none" stroke="#facc15" stroke-width="2" />
            </g>
            <g :transform="`translate(0, ${h/2})`">
               <path d="M -10,-5 L 0,0 L -10,5" fill="none" stroke="#facc15" stroke-width="2" />
               <path d="M 10,-5 L 20,0 L 10,5" fill="none" stroke="#facc15" stroke-width="2" />
            </g>

            <!-- Legs markers -->
            <line :x1="- (wT/2 + wB/2)/2 - 10" :y1="-5" :x2="- (wT/2 + wB/2)/2 + 10" :y2="5" stroke="#fb7185" stroke-width="3" />
            <line :x1="(wT/2 + wB/2)/2 - 10" :y1="-5" :x2="(wT/2 + wB/2)/2 + 10" :y2="5" stroke="#fb7185" stroke-width="3" />

            <!-- Vertices & Labels -->
            <circle :cx="-wT/2" :cy="-h/2" r="5" fill="#fff" />
            <text :x="-wT/2 - 25" :y="-h/2 - 10" fill="#cbd5e1" font-weight="bold">A</text>
            
            <circle :cx="wT/2" :cy="-h/2" r="5" fill="#fff" />
            <text :x="wT/2 + 15" :y="-h/2 - 10" fill="#cbd5e1" font-weight="bold">D</text>

            <circle :cx="wB/2" :cy="h/2" r="5" fill="#fff" />
            <text :x="wB/2 + 15" :y="h/2 + 25" fill="#cbd5e1" font-weight="bold">C</text>

            <circle :cx="-wB/2" :cy="h/2" r="5" fill="#fff" />
            <text :x="-wB/2 - 25" :y="h/2 + 25" fill="#cbd5e1" font-weight="bold">B</text>

            <!-- Diagonals check -->
            <g v-if="showDiagonals">
               <line :x1="-wT/2" :y1="-h/2" :x2="wB/2" :y2="h/2" stroke="rgba(248, 250, 252, 0.4)" stroke-width="1.5" stroke-dasharray="4" />
               <line :x1="wT/2" :y1="-h/2" :x2="-wB/2" :y2="h/2" stroke="rgba(248, 250, 252, 0.4)" stroke-width="1.5" stroke-dasharray="4" />
               <text x="0" y="-10" fill="#f8fafc" font-size="10" text-anchor="middle">AC = BD</text>
            </g>

            <!-- Base Angles -->
            <g v-if="showAngles" fill="none" stroke="#a78bfa" stroke-width="2">
               <path :d="`M ${-wB/2 + 25},${h/2} A 25 25 0 0 0 ${-wB/2 + 20},${h/2 - 15}`" />
               <path :d="`M ${wB/2 - 25},${h/2} A 25 25 0 0 1 ${wB/2 - 20},${h/2 - 15}`" />
               <text :x="-wB/2 + 35" :y="h/2 - 10" fill="#a78bfa" font-size="12" font-weight="bold">∠B</text>
               <text :x="wB/2 - 50" :y="h/2 - 10" fill="#a78bfa" font-size="12" font-weight="bold">∠C</text>
            </g>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>腰长 AB/CD</label>
              <span class="val">{{ legLength.toFixed(1) }} <small>px</small></span>
           </div>
           <div class="hud-item orange">
              <label>底角 ∠B/∠C</label>
              <span class="val">{{ angleB.toFixed(1) }}°</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>上底长度 AD</label>
                 <el-slider v-model="wT" :min="40" :max="250" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>下底长度 BC</label>
                 <el-slider v-model="wB" :min="100" :max="450" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>梯形高度 h</label>
                 <el-slider v-model="h" :min="50" :max="200" size="small"></el-slider>
              </div>
              <div class="s-item flex-row items-center justify-around">
                 <el-checkbox v-model="showDiagonals">显示对角线</el-checkbox>
                 <el-checkbox v-model="showAngles">标注底角</el-checkbox>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>数学核心判定：</strong>等腰梯形是特殊的梯形。它的判定条件主要有二：一是<strong>两腰相等</strong>的梯形；二是在同一底上的<strong>两个角相等</strong>。性质上，它不仅拥有相等的对角线，还具备完美的<strong>轴对称性</strong>——对称轴是上下底中点的连线。通过调节滑块，你可以发现无论如何改变长宽，左右两边的对称美始终维持。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IsoscelesTrapezoidViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      wT: 100,
      wB: 300,
      h: 120,
      showDiagonals: false,
      showAngles: false
    }
  },
  computed: {
    trapezoidPath() {
       return `M ${-this.wT/2} ${-this.h/2} L ${this.wT/2} ${-this.h/2} L ${this.wB/2} ${this.h/2} L ${-this.wB/2} ${this.h/2} Z`;
    },
    legLength() {
       let dx = (this.wB - this.wT) / 2;
       return Math.sqrt(dx*dx + this.h*this.h);
    },
    angleB() {
       let dx = (this.wB - this.wT) / 2;
       return Math.atan2(this.h, dx) * (180 / Math.PI);
    }
  }
}
</script>

<style scoped lang="scss">
.quad-viz-modern { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .justify-around { justify-content: space-around; }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: flex-start; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>